import { settleReleasedSubtree } from './releaseSettling';

function workerError(error) {
  if (error instanceof Error) return error;
  return new Error(error?.message || 'Release settlement worker failed');
}

/**
 * Owns at most one release-settlement worker. Starting another request
 * terminates the previous worker, so an expensive stale calculation does not
 * block the newest drop behind it.
 */
export function createReleaseSettlementController({
  createWorker,
  fallback = settleReleasedSubtree,
  getVersion = () => null,
} = {}) {
  if (typeof createWorker !== 'function') {
    throw new TypeError('createWorker must be a function');
  }

  let nextRequestId = 1;
  let active = null;
  let disposed = false;

  const finish = (request, outcome) => {
    if (active?.requestId === request.requestId) active = null;
    request.worker?.terminate?.();
    request.resolve(outcome);
  };

  const cancel = (reason = 'cancelled') => {
    if (!active) return false;
    const request = active;
    finish(request, {
      status: 'cancelled',
      reason,
      requestId: request.requestId,
      version: request.version,
    });
    return true;
  };

  const settle = ({ command, options } = {}) => {
    if (disposed) {
      return Promise.resolve({ status: 'cancelled', reason: 'disposed', requestId: null });
    }
    cancel('superseded');
    const requestId = nextRequestId;
    nextRequestId += 1;
    const version = getVersion();

    return new Promise((resolve, reject) => {
      const runFallback = () => {
        try {
          resolve({
            status: 'completed',
            requestId,
            version,
            source: 'fallback',
            result: fallback(command, options),
          });
        } catch (error) {
          reject(workerError(error));
        }
      };

      let worker;
      try {
        worker = createWorker();
      } catch {
        runFallback();
        return;
      }
      if (!worker || typeof worker.postMessage !== 'function') {
        worker?.terminate?.();
        runFallback();
        return;
      }

      const request = { requestId, version, worker, resolve, reject };
      active = request;
      worker.onmessage = (event) => {
        if (active?.requestId !== requestId) return;
        const message = event?.data;
        if (message?.requestId !== requestId) return;
        if (message.type === 'result') {
          if (message.version !== version || getVersion() !== version) {
            finish(request, {
              status: 'cancelled',
              reason: 'stale-graph',
              requestId,
              version,
            });
            return;
          }
          finish(request, {
            status: 'completed',
            requestId,
            version,
            source: 'worker',
            result: message.result,
          });
          return;
        }
        if (message.type === 'error') {
          if (getVersion() !== version) {
            finish(request, {
              status: 'cancelled',
              reason: 'stale-graph',
              requestId,
              version,
            });
            return;
          }
          active = null;
          worker.terminate?.();
          try {
            resolve({
              status: 'completed',
              requestId,
              version,
              source: 'fallback',
              result: fallback(command, options),
            });
          } catch (error) {
            reject(workerError(error));
          }
        }
      };
      worker.onerror = (error) => {
        if (active?.requestId !== requestId) return;
        error?.preventDefault?.();
        if (getVersion() !== version) {
          finish(request, {
            status: 'cancelled',
            reason: 'stale-graph',
            requestId,
            version,
          });
          return;
        }
        active = null;
        worker.terminate?.();
        try {
          resolve({
            status: 'completed',
            requestId,
            version,
            source: 'fallback',
            result: fallback(command, options),
          });
        } catch (fallbackError) {
          reject(workerError(fallbackError));
        }
      };

      try {
        worker.postMessage({
          type: 'settle-release',
          requestId,
          version,
          command,
          options,
        });
      } catch {
        active = null;
        worker.terminate?.();
        runFallback();
      }
    });
  };

  const dispose = () => {
    disposed = true;
    cancel('disposed');
  };

  return Object.freeze({ settle, cancel, dispose });
}
